import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import classNames from "classnames";
import { UXPContext } from "@core/context/Context";
import Icon from "@icon/Icon";
import IconPickerModal from "./IconPickerModal";
import FormFeedback from "@widget/forms/inputs/FormFeedback";
import DropdownIndicator from "@widget/other/DropdownIndicator";
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import { getInputSizeStyles, getInputStateClass } from "../utils";
/**
 * @export
 * IconPicker component - A form input for selecting FontAwesome and Phosphor icons
 *
 * @example
 * ```tsx
 * <IconPicker
 *   value="fas:bell"
 *   onChange={(iconStr) => setIcon(iconStr)}
 *   placeholder="Select an icon"
 * />
 * ```
 */
const IconPicker = (props) => {
    const { value, onChange, label, disabled, readOnly, isValid: isValidProp, className, minWidth, maxWidth, width, fullWidth, defaultViewMode, hideLabel = false, compactMode = false, placeholder, onClear } = props;
    const context = React.useContext(UXPContext);
    const [showModal, setShowModal] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);
    const [normalizedValue, setNormalizedValue] = React.useState(null);
    const [metadataError, setMetadataError] = React.useState(false);
    // Check if metadata is loaded
    const isMetadataLoaded = React.useMemo(() => {
        return typeof window !== 'undefined' && !!window.IconMetadata;
    }, []);
    // Normalize legacy values on mount or when value changes
    React.useEffect(() => {
        if (!value) {
            setNormalizedValue(null);
            return;
        }
        // Check if metadata is available
        if (!isMetadataLoaded) {
            setMetadataError(true);
            setNormalizedValue(value); // Use as-is if metadata not loaded
            return;
        }
        const IconMetadata = window.IconMetadata;
        // Check if it's a legacy key
        if (IconMetadata.isLegacyKey(value)) {
            const converted = IconMetadata.convertLegacyKey(value);
            if (converted) {
                setNormalizedValue(converted);
                // Auto-update parent with new format
                onChange(converted);
            }
            else {
                setNormalizedValue(value); // Fallback if conversion fails
            }
        }
        else {
            setNormalizedValue(value);
        }
        setMetadataError(false);
    }, [value, isMetadataLoaded, onChange]);
    const handleClick = () => {
        if (disabled || readOnly)
            return;
        if (!isMetadataLoaded) {
            setMetadataError(true);
            return;
        }
        setIsFocused(true);
        setShowModal(true);
    };
    const handleSelect = (iconString) => {
        setNormalizedValue(iconString);
        onChange(iconString);
        setShowModal(false);
        setIsFocused(false);
    };
    const handleCloseModal = () => {
        setShowModal(false);
        setIsFocused(false);
    };
    // Calculate state classes
    const stateClass = getInputStateClass({ disabled, readOnly, isValid: isValidProp });
    // Determine if we show the dropdown indicator (only in standard mode when label is visible)
    const showDropdownIndicator = !compactMode && !hideLabel;
    const clearButton = (!!normalizedValue && !compactMode && !disabled && !readOnly) ? (_jsx("div", { className: "uxpcore_input__clear-btn", onClick: (e) => { e.stopPropagation(); onClear ? onClear() : onChange(''); }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'times'] }) })) : null;
    return (_jsxs("div", { className: classNames("uxpcore_iconpicker", className, {
            'uxpcore_input--full-width': fullWidth
        }), style: compactMode ? {} : getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined }), children: [_jsx("div", { className: classNames("uxpcore_input", compactMode ? "uxpcore_input--iconpicker-compact" : "uxpcore_input--iconpicker", stateClass, {
                    "uxpcore_input--has-prefix": !compactMode && !hideLabel,
                    "uxpcore_input--has-suffix": !!(showDropdownIndicator || clearButton),
                    "uxpcore_input--has-content": !!normalizedValue,
                    "uxpcore_input--focus": isFocused,
                    "uxpcore_input--error": metadataError,
                    "uxpcore_input--icon-only": !compactMode && hideLabel
                }), onClick: handleClick, title: label || context.$L("uxp-core.icon-picker.select-icon"), role: "button", "aria-label": label || context.$L("uxp-core.icon-picker.select-icon"), tabIndex: disabled ? -1 : 0, children: compactMode ? (
                // Compact square mode: just the icon, centered
                _jsx(Icon, { icon: normalizedValue || "phl:question", size: "2x" })) : (
                // Standard input height mode: icon in prefix + optional label
                _jsxs(_Fragment, { children: [_jsx("div", { className: "uxpcore_input__prefix", children: _jsx(Icon, { icon: normalizedValue || "phl:question", size: "lg" }) }), !hideLabel && (_jsx("div", { className: "uxpcore_input__value", children: normalizedValue || placeholder || context.$L("uxp-core.icon-picker.select-icon") })), (showDropdownIndicator || clearButton) && (_jsxs("div", { className: "uxpcore_input__suffix", children: [clearButton, showDropdownIndicator && _jsx(DropdownIndicator, { isOpen: isFocused })] }))] })) }), metadataError && (_jsx(FormFeedback, { validInput: false, children: context.$L("uxp-core.icon-picker.error") })), isMetadataLoaded && (_jsx(IconPickerModal, { show: showModal, onClose: handleCloseModal, onSelect: handleSelect, currentValue: normalizedValue || undefined, defaultViewMode: defaultViewMode }))] }));
};
export default IconPicker;
